import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import { GenericTable } from "@ui-components/Table/GenericTable";
import { Auth0User, auth0UsersColumns, ActionsDropdown } from "./UsersData";
import { getAuth0Users } from "./request/request";
import { GenericFilter } from "@ui-components/Filter/GenericFilter";
import GenericButton from "@ui-components/Button/GenericButton";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  clearUsersDirty,
  selectUsersDirty,
} from "@redux/slices/usersListSlice";
import { openEditor } from "@redux/slices/userEditorSlice";

const API_URL = process.env.REACT_APP_API_URL || "";

interface RawUser {
  user_id: string;
  name: string;
  email: string;
  hasMFA: boolean;
  blocked: boolean;
  roles: Array<{
    id: number;
    name: string;
  }>;
}

type AlertConfig = {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "confirm" | "success" | "error" | "warning";
  resolve: (value: boolean) => void;
};

export function UsersPage() {
  // const tenantId = useAppSelector((state) => state.tenant.id);
  const { tenantId } = useAppSelector((state) => state.selection);
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [isFetching, setIsFetching] = useState(false);
  const [usersData, setUsersData] = useState<Auth0User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchColumn, setSearchColumn] = useState("All");
  const [ssoActive, setSsoActive] = useState(false);
  const overlaySpinner = useProcessingOverlay(isFetching);
  const dispatch = useAppDispatch();
  const usersDirty = useAppSelector(selectUsersDirty);
  // const [editUserId, setEditUserId] = useState<string | null | undefined>(undefined);
  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);

  const showConfirmationAlert = (
    config: Omit<AlertConfig, "resolve">
  ): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setAlertConfig({ ...config, resolve });
    });
  };

  const showAlert = (config: Omit<AlertConfig, "resolve">): Promise<void> => {
    return new Promise<void>((resolve) => {
      setAlertConfig({ ...config, resolve: () => resolve() });
    });
  };

  const fetchUsers = async () => {
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      const resp = await getAuth0Users(token, tenantId);
      const raw = resp.data;
      const userList = Array.isArray(raw.users) ? (raw.users as RawUser[]) : [];
      setSsoActive(raw.sso_active || false);
      const shapedData: Auth0User[] = userList.map((u: RawUser) => ({
        user_id: u.user_id,
        name: u.name,
        email: u.email,
        hasMFA: u.hasMFA,
        status: u.blocked ? "blocked" : "active",
        roles: u.roles,
      }));
      setUsersData(shapedData);
    } catch (err) {
      console.error("Error fetching Auth0 users =>", err);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;
    if (tenantId === 0) return;
    fetchUsers();
  }, [isLoading, isAuthenticated, tenantId]);

  const handleRefresh = async () => {
    await fetchUsers();
  };

  useEffect(() => {
    if (usersDirty) {
      // Someone saved via the global modal -> refresh the table
      fetchUsers().finally(() => dispatch(clearUsersDirty()));
    }
  }, [usersDirty]);

  const filteredData = usersData.filter((row) => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    const includes = (val: string) => val.toLowerCase().includes(term);
    switch (searchColumn) {
      case "Name":
        return includes(row.name);
      case "Email":
        return includes(row.email);
      case "Status":
        return includes(row.status);
      case "Role":
        return row.roles.some((r) => includes(r.name));
      default:
        return (
          includes(row.name) ||
          includes(row.email) ||
          includes(row.status) ||
          row.roles.some((r) => includes(r.name))
        );
    }
  });

  async function handleDelete(userId: string) {
    const confirmed = await showConfirmationAlert({
      title: "Are you sure?",
      description: "This will delete the user permanently!",
      confirmLabel: "Yes, delete it!",
      cancelLabel: "Cancel",
      variant: "warning",
    });
    if (!confirmed) return;
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      const deleteUrl =
        tenantId === 0
          ? `${API_URL}/v1/auth0/users/delete/${userId}`
          : `${API_URL}/v1/auth0/users/delete/${userId}?tenantId=${tenantId}`;
      await axios.delete(deleteUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await showAlert({
        title: "Deleted!",
        description: "User has been removed.",
        confirmLabel: "OK",
        variant: "success",
      });
      await fetchUsers();
    } catch (err) {
      await showAlert({
        title: "Error!",
        description: "Failed to delete user.",
        confirmLabel: "OK",
        variant: "error",
      });
    } finally {
      setIsFetching(false);
    }
  }

  async function handleBlock(userId: string, isBlocked: boolean) {
    const confirmed = await showConfirmationAlert({
      title: `Are you sure you want to ${
        isBlocked ? "unblock" : "block"
      } this user?`,
      description: "",
      confirmLabel: `Yes, ${isBlocked ? "unblock" : "block"} it!`,
      cancelLabel: "Cancel",
      variant: "warning",
    });
    if (!confirmed) return;
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      await axios.put(
        `${API_URL}/v1/auth0/users/block`,
        { userId, block: !isBlocked },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      await showAlert({
        title: `User ${isBlocked ? "unblocked" : "blocked"}!`,
        description: "",
        confirmLabel: "OK",
        variant: "success",
      });
      await fetchUsers();
    } catch (err) {
      await showAlert({
        title: "Error!",
        description: `Failed to ${isBlocked ? "unblock" : "block"} user.`,
        confirmLabel: "OK",
        variant: "error",
      });
    } finally {
      setIsFetching(false);
    }
  }

  async function handleResetPassword(email: string) {
    const confirmed = await showConfirmationAlert({
      title: `Reset password for ${email}?`,
      description: "",
      confirmLabel: "Yes, reset!",
      cancelLabel: "Cancel",
      variant: "warning",
    });
    if (!confirmed) return;
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      const endpoint = `${API_URL}/v1/auth0/users/reset-password${
        tenantId ? `?tenantId=${tenantId}` : ""
      }`;
      await axios.post(
        endpoint,
        { email },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      await showAlert({
        title: "Password Reset!",
        description: "An email to reset the password has been sent.",
        confirmLabel: "OK",
        variant: "success",
      });
    } catch (err) {
      await showAlert({
        title: "Error!",
        description: "Failed to send password reset email.",
        confirmLabel: "OK",
        variant: "error",
      });
    } finally {
      setIsFetching(false);
    }
  }

  async function handleResetMFA(userId: string) {
    const confirmed = await showConfirmationAlert({
      title: "Reset MFA?",
      description: "This will clear the MFA settings for the user.",
      confirmLabel: "Yes, reset!",
      cancelLabel: "Cancel",
      variant: "warning",
    });
    if (!confirmed) return;
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      await axios.delete(`${API_URL}/v1/auth0/users/mfa-reset/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await showAlert({
        title: "MFA Reset!",
        description: "The MFA settings have been cleared.",
        confirmLabel: "OK",
        variant: "success",
      });
    } catch (err) {
      await showAlert({
        title: "Error!",
        description: "Failed to reset MFA.",
        confirmLabel: "OK",
        variant: "error",
      });
    } finally {
      setIsFetching(false);
    }
  }

  const modifiedColumns = auth0UsersColumns.map((col) => {
    if (col.header === "Actions") {
      return {
        ...col,
        renderCell: (row: Auth0User) => (
          <div className="px-4 py-2">
            <ActionsDropdown
              row={row}
              onEdit={(id: string) => dispatch(openEditor(id))}
              onDelete={handleDelete}
              onBlock={handleBlock}
              onResetPassword={handleResetPassword}
              onResetMFA={handleResetMFA}
              ssoActive={ssoActive}
            />
          </div>
        ),
      };
    }
    return col;
  });

  return (
    <>
      <div className="relative">
        <GenericCard
          variant="primary"
          shadow="sm"
          hasBorder
          isCollapsible={false}
          headerContent={
            <div className="flex items-center justify-end w-full">
              <div className="flex gap-3">
                <GenericButton
                  label="Refresh"
                  variant="secondary"
                  shape="ghost"
                  size="md"
                  onClick={handleRefresh}
                />
                <GenericButton
                  label="Add User"
                  variant="brand"
                  size="md"
                  onClick={() => dispatch(openEditor(null))}
                  disabled={ssoActive}
                  title={
                    ssoActive
                      ? "User addition is disabled because SSO is active. Please use the configured Identity Provider (IdP) to manage user accounts."
                      : ""
                  }
                />
              </div>
            </div>
          }
          bodyContent={
            <>
              <div className="mb-4">
                <div className="flex items-end justify-between">
                  <div className="flex items-end space-x-4" />
                  <div className="flex items-center space-x-4">
                    <GenericFilter
                      searchColumn={searchColumn}
                      onSearchColumnChange={setSearchColumn}
                      searchTerm={searchTerm}
                      onSearchTermChange={setSearchTerm}
                      columnOptions={["All", "Name", "Email", "Status", "Role"]}
                    />
                  </div>
                </div>
              </div>
              <GenericTable
                columns={modifiedColumns}
                data={filteredData}
                initialRowsPerPage={10}
                rowsPerPageOptions={[5, 10]}
                width="100%"
                maxTableBodyHeight="500px"
                tableClassName="bg-semantic-bg-primary"
                overflow="hidden"
              />
            </>
          }
        />
      </div>
      {overlaySpinner}
      {alertConfig && (
        <GenericAlert
          isOpen={true}
          onConfirm={() => {
            alertConfig.resolve(true);
            setAlertConfig(null);
          }}
          onClose={() => {
            alertConfig.resolve(false);
            setAlertConfig(null);
          }}
          title={alertConfig.title}
          description={alertConfig.description}
          confirmLabel={alertConfig.confirmLabel}
          cancelLabel={alertConfig.cancelLabel}
          variant={alertConfig.variant}
        />
      )}
    </>
  );
}

export default UsersPage;
